import { zoneScene } from '@pz/scene';
import { el, note } from '../panels.js';
import { fillText } from '../texts.js';
import { renderReview } from '../review.js';
import { toolFor } from '../commands.js';
import { openSheet, type SheetHandle } from './sheet.js';
import type { PhoneEnv } from './types-phone.js';

/** How many pixels across the review picture is allowed to be. */
const REVIEW_PX = 900;

/**
 * The Pregled sheet: what the shopper is about to buy, the four things Zazzle
 * asks them to check, every blocking problem with a way to jump to it, and the
 * primary "Gotovo".
 *
 * The picture is the host's product preview when the host offers one (the
 * design on the mug), and otherwise the print composition rendered by the same
 * Skia that makes the print file — never a DOM approximation, because the
 * whole point of this sheet is that nothing surprises anybody at the till.
 */
export function openReviewSheet(env: PhoneEnv): SheetHandle {
  const ctx = env.ctx;
  const t = env.texts;

  return openSheet({
    root: env.root,
    title: t.reviewTitle,
    closeLabel: t.close,
    tall: true,
    build: (body) => {
      let dead = false;
      // The surcharge in full, where the shopper is about to accept it; the
      // top bar only carries the number as a caption under GOTOVO.
      const price = env.actions.price();
      if (price) {
        const line = el('span', 'pzd-ph-review-price', fillText(ctx.texts.price, { price }));
        body.append(line);
      }
      const shot = el('canvas', 'pzd-ph-review-shot');
      const status = note(t.reviewRendering);
      body.append(shot, status);
      shot.hidden = true;
      void paint(shot, status);

      renderReview(body, ctx, {
        multiSurface: env.surfaces.length > 1,
        done: () => { env.closeSheet(); env.actions.done(); },
        fix: problem => {
          env.closeSheet();
          const layer = ctx.store.value().layers.find(item => item.id === problem.layerId);
          if (!layer) { env.actions.explainFirstProblem(); return; }
          ctx.setHidden(layer.id, false);
          ctx.dispatch({ type: 'select', id: layer.id });
          ctx.openTool(toolFor(layer));
        },
      });

      /** Draws the product preview, or the print composition, or says why not. */
      async function paint(canvas: HTMLCanvasElement, label: HTMLElement): Promise<void> {
        const value = ctx.store.value();
        try {
          const url = await env.actions.previewProduct?.(value);
          if (dead) return;
          if (url) {
            const picture = el('img', 'pzd-ph-review-shot');
            picture.src = url;
            picture.alt = t.reviewTitle;
            canvas.replaceWith(picture);
            label.remove();
            return;
          }
        } catch {
          /* fall through to the print composition */
        }
        const density = Math.min(3, REVIEW_PX / Math.max(1, ctx.config.sizeMm.w, ctx.config.sizeMm.h));
        try {
          const out = await ctx.renderer.renderRaw?.(zoneScene(ctx.rawConfig, value, density), 1);
          if (dead) return;
          if (!out) {
            label.textContent = t.reviewFailed;
            return;
          }
          canvas.width = out.width;
          canvas.height = out.height;
          const paper = canvas.getContext('2d');
          if (!paper) {
            label.textContent = t.reviewFailed;
            return;
          }
          const picture = paper.createImageData(out.width, out.height);
          picture.data.set(out.pixels);
          paper.putImageData(picture, 0, 0);
          canvas.hidden = false;
          canvas.setAttribute('role', 'img');
          canvas.setAttribute('aria-label', t.reviewTitle);
          label.remove();
        } catch {
          if (!dead) label.textContent = t.reviewFailed;
        }
      }

      return () => {
        dead = true;
      };
    },
  });
}
